/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import { useMutation } from '@apollo/client';

import user from 'js/lib/user';

import { InlineNotification, StandaloneCheckbox, Typography2 } from '@coursera/cds-core';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import { receivedEmailPreferences } from 'bundles/account-settings/actions/SettingsServerActionCreators';
import { Sections, UpdateStates } from 'bundles/account-settings/constants/SettingsConstants';
import type { UpdateNotificationPreferencesMutationVariables } from 'bundles/account-settings/queries';
import { UpdateNotificationPreferencesMutation } from 'bundles/account-settings/queries';
import SettingsStore from 'bundles/account-settings/stores/SettingsStore';
import type {
  EmailPreference,
  GlobalNotificationPreferences,
  UpdateStatus,
} from 'bundles/account-settings/types/EmailPreferences';
import { NotificationPreferenceChannels } from 'bundles/account-settings/types/EmailPreferences';
import { preparePreferences } from 'bundles/account-settings/utils/SettingsAPIUtils';
import useDebounce from 'bundles/common/hooks/useDebounce';
import { getStatusMessages } from 'bundles/user-account/utils/errorMessageUtils';

import _t from 'i18n!nls/account-settings';

type Props = {
  preferences: Pick<GlobalNotificationPreferences, 'promotions'>;
  onChange?: (value: boolean) => void;
};

const styles = {
  title: css`
    border: 0;
    margin-block-end: var(--cds-spacing-100);
  `,
  supportText: css`
    margin-block-end: var(--cds-spacing-200);
  `,
  inlineNotification: css`
    margin-block-start: var(--cds-spacing-200);
  `,
};

const GlobalPushNotificationPreference: React.FC<Props> = ({ preferences, onChange }) => {
  const userId = user.get().id.toString();
  const title = _t('Push notifications');
  const supportText = _t('You can opt in and out of all the push notifications sent by Coursera.');
  const hasEnabledPushNotifications = Object.values(preferences).some(Boolean);
  const [checked, setChecked] = useState<boolean>(() => hasEnabledPushNotifications || false);
  const [preferencesUpdateStatus, setPreferencesUpdateStatus] = useState<UpdateStatus>({ state: UpdateStates.IDLE });

  const track = useTracker();
  const checkboxRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker('view_checkbox', {
    checkbox: {
      name: 'global_push_notifications_preference',
      checked,
    },
  });

  const handleOnChange = (isChecked: boolean) => {
    onChange?.(isChecked);
    setChecked(isChecked);
  };

  const [updatePrefences] = useMutation<$TSFixMe, UpdateNotificationPreferencesMutationVariables>(
    UpdateNotificationPreferencesMutation,
    {
      onCompleted: (data) => {
        const updatedPreferences = data?.UpdateNotificationPreferences?.elements[0];
        const emailPreferences = SettingsStore.getEmailPreferences();

        setPreferencesUpdateStatus({ state: UpdateStates.SUCCEEDED });
        receivedEmailPreferences(updatedPreferences, {
          ...emailPreferences,
          coursera: {
            ...emailPreferences.coursera,
            promotions: { ...emailPreferences.coursera.promotions, push: checked },
          },
        });
      },
      onError: () => {
        setPreferencesUpdateStatus({ state: UpdateStates.FAILED });
        handleOnChange(hasEnabledPushNotifications);
      },
    }
  );

  const togglePushPreferences = useDebounce(() => {
    const updatedPreferences = preparePreferences(
      Sections.COURSERA_EMAIL_PREFERENCES,
      [{ id: 'promotions', value: { promotions: checked } } as EmailPreference],
      NotificationPreferenceChannels.PUSH
    );
    updatePrefences({ variables: { userId, body: updatedPreferences } });
  }, 500);

  const handleTogglePushPreferences = (e: React.ChangeEvent<HTMLInputElement>, isChecked: boolean) => {
    handleOnChange(isChecked);
    togglePushPreferences();
    track('click_checkbox', {
      checkbox: {
        name: 'global_push_notifications_preference',
        checked: isChecked,
      },
    });
  };

  return (
    <fieldset aria-label={`${title}. ${supportText}`}>
      <Typography2 component="legend" variant="subtitleLarge" css={styles.title}>
        {title}
      </Typography2>
      <Typography2 component="p" css={styles.supportText}>
        {supportText}
      </Typography2>
      <StandaloneCheckbox
        ref={checkboxRef}
        optional
        label={_t('Send me Marketing push notifications')}
        checked={checked}
        onChange={handleTogglePushPreferences}
      />
      <div aria-live="polite">
        {preferencesUpdateStatus.state === UpdateStates.FAILED && (
          <InlineNotification css={styles.inlineNotification} severity="error">
            {getStatusMessages.UPDATE_FAILED}
          </InlineNotification>
        )}

        {preferencesUpdateStatus.state === UpdateStates.SUCCEEDED && (
          <InlineNotification css={styles.inlineNotification} severity="success">
            {getStatusMessages.UPDATE_SUCCEEDED}
          </InlineNotification>
        )}
      </div>
    </fieldset>
  );
};

export default GlobalPushNotificationPreference;
